import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { CheckCircle2, AlertCircle, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PaymentSuccess() {
  const navigate = useNavigate();
  const [state, setState] = useState({ status: 'verifying', message: 'Verifying your payment...' });

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get('session_id');

    if (!sessionId) {
      setState({ status: 'error', message: 'Missing session ID. If you completed payment, please contact your coach.' });
      return;
    }

    const verify = async () => {
      try {
        const res = await axios.post(`${API}/payments/verify-trial-upgrade/${sessionId}`);
        const data = res.data;
        if (data.payment_status === 'paid') {
          // Clear any cached client_user so dashboard re-fetches fresh trial state on next load
          try { localStorage.removeItem('client_user'); } catch (_) {}
          setState({ status: 'success', message: data.message || 'Subscription activated! Welcome aboard.' });
        } else if (data.status === 'pending') {
          setState({ status: 'pending', message: 'Your payment is still processing. Please refresh in a minute.' });
        } else {
          setState({ status: 'success', message: data.message || 'Subscription activated.' });
        }
      } catch (err) {
        const detail = err.response?.data?.detail || err.message || 'Verification failed';
        setState({ status: 'error', message: `Could not verify your payment: ${detail}. Please contact your coach.` });
      }
    };
    verify();
  }, []);

  const isAuthed = typeof window !== 'undefined' && !!localStorage.getItem('client_token');

  return (
    <div data-testid="payment-success-page" className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-cyan-50 px-4">
      <div className="bg-white rounded-2xl shadow-xl p-10 max-w-md w-full text-center">
        {state.status === 'verifying' && (
          <>
            <Loader2 className="h-14 w-14 mx-auto text-blue-600 animate-spin mb-4" />
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Verifying payment...</h1>
            <p className="text-gray-600">{state.message}</p>
          </>
        )}
        {state.status === 'success' && (
          <>
            <CheckCircle2 className="h-14 w-14 mx-auto text-green-600 mb-4" />
            <h1 className="text-2xl font-bold text-gray-900 mb-2">You're all set!</h1>
            <p className="text-gray-600 mb-6">{state.message}</p>
            <Button
              data-testid="payment-success-continue-btn"
              onClick={() => navigate(isAuthed ? '/app' : '/app/login')}
              className="w-full bg-blue-600 hover:bg-blue-700"
              size="lg"
            >
              {isAuthed ? 'Go to Dashboard' : 'Log in to your account'}
            </Button>
          </>
        )}
        {state.status === 'pending' && (
          <>
            <Loader2 className="h-14 w-14 mx-auto text-yellow-600 mb-4" />
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Payment processing</h1>
            <p className="text-gray-600 mb-6">{state.message}</p>
            <Button onClick={() => window.location.reload()} className="w-full" size="lg">Check again</Button>
          </>
        )}
        {state.status === 'error' && (
          <>
            <AlertCircle className="h-14 w-14 mx-auto text-red-600 mb-4" />
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Something went wrong</h1>
            <p className="text-gray-600 mb-6">{state.message}</p>
            <Button onClick={() => navigate('/app/login')} className="w-full" size="lg">Go to login</Button>
          </>
        )}
      </div>
    </div>
  );
}
